import { financial_threshold_edit_catalog } from './delegation_data';
import {
  delegation_of_authority_hooks,
  digits_only,
  get_progress_dot_class,
  get_progress_width_class,
  parse_range_band
} from './delegation_progress_helpers';

function approval_matrix_second_approver(row) {
  if (row.edit_second_approver) return row.edit_second_approver;
  if (row.approval.includes('+')) return row.approval.split('+').slice(1).join('+').trim();
  return 'Optional';
}

function value_or_fallback(value, fallback_value) {
  return value || fallback_value;
}

function build_approval_matrix_overrides(row) {
  const approval_matrix_overrides = {
    'Transaction type': row.transaction,
    Category: row.category,
    Department: row.dept,
    'Amount band / condition': row.band,
    'Risk level': row.risk,
    'Required approver': row.approval,
    'Second approver (if dual)': approval_matrix_second_approver(row),
    Note: row.edit_note || 'Auto-generated rule'
  };
  return approval_matrix_overrides;
}

function build_financial_threshold_overrides(group, card) {
  const edit_group = Reflect.get(financial_threshold_edit_catalog, group.label);
  const limit = Reflect.get(edit_group?.band_to_limits || {}, card.band) || null;
  const approval_limit_record = {
    Category: value_or_fallback(edit_group?.category, group.label),
    'From (R, inclusive)': limit?.from,
    'To (R, exclusive - blank = no ceiling)': limit?.to,
    Approver: value_or_fallback(limit?.approver, card.approver),
    'Second approver': value_or_fallback(limit?.second, 'Optional')
  };

  return approval_limit_record;
}

function fill_missing_band_limits(overrides, band, normalize_band_value) {
  const from_label = 'From (R, inclusive)';
  const to_label = 'To (R, exclusive - blank = no ceiling)';
  if (Reflect.get(overrides, from_label) || Reflect.get(overrides, to_label)) {
    return overrides;
  }
  Reflect.set(overrides, from_label, normalize_band_value(band, 'from'));
  Reflect.set(overrides, to_label, normalize_band_value(band, 'to'));
  const updated_overrides = overrides;
  return updated_overrides;
}

function build_exception_edit_overrides(item) {
  const what_happened = value_or_fallback(item.what_happened, item.conflict || '');
  const proper_authority = value_or_fallback(item.should_have_been, '');
  const resolved_what_happened = value_or_fallback(what_happened, proper_authority);
  const exception_edit_overrides = {
    Transaction: item.transaction,
    Value: item.value,
    'What happened': resolved_what_happened,
    'Approved by (actual)': item.approved_by,
    'Proper authority (required)': proper_authority,
    Reason: value_or_fallback(item.reason, 'Justification given'),
    'Escalated to': item.escalated_to,
    Status: item.status.label
  };
  return exception_edit_overrides;
}

function duty_value(chip_values, index) {
  const duty = Reflect.get(chip_values, index) || '';
  return duty;
}

function normalize_above_band_value(compact_band, side) {
  if (side !== 'from') return '';
  const above_band_value = digits_only(compact_band.replace(/^Above\s+/i, ''));
  return above_band_value;
}

export const delegation_of_authority_methods = {
    set_tab(tab_id) {
      this.active_tab = tab_id;
    },

    open_summary_card(card) {
      if (!card?.tab_id) {
        return;
      }

      this.set_tab(card.tab_id);
    },

    open_app_route(route_path) {
      if (!route_path || !this.$router) {
        return;
      }

      this.$router.push(route_path);
    },

    open_drawer(drawer_key) {
      if (!drawer_key) {
        return;
      }

      this.active_drawer_key = drawer_key;
      this.$nextTick(() => {
        const drawer_body = this.$refs.drawerBody;

        if (drawer_body) {
          drawer_body.scrollTop = 0;
        }
      });
    },

    open_modal(modal_key) {
      if (!modal_key) {
        return;
      }

      this.active_modal_key = modal_key;
      this.active_modal_data = this.clone_modal_definition(modal_key);
      document.body.classList.add('doa-modal-open');
    },

    open_edit_modal(modal_key, overrides) {
      if (!modal_key) {
        return;
      }

      this.active_modal_key = modal_key;
      this.active_modal_data = this.clone_modal_definition(modal_key, overrides);
      document.body.classList.add('doa-modal-open');
    },

    open_approval_matrix_edit(row) {
      if (!row) {
        return;
      }

      this.open_edit_modal('approval_rule_edit', build_approval_matrix_overrides(row));
    },

    open_financial_threshold_edit(group, card) {
      if (!group || !card) {
        return;
      }

      const overrides = build_financial_threshold_overrides(group, card);
      fill_missing_band_limits(overrides, card.band, this.normalize_band_value);
      this.open_edit_modal('financial_threshold_edit', overrides);
    },

    open_procurement_authority_edit(panel, row) {
      if (!panel || !row) {
        return;
      }

      this.open_edit_modal(panel.modal_key === 'contract_authority' ? 'contract_authority_edit' : 'procurement_authority_edit', {
        Action: row.action,
        Condition: row.condition,
        Approver: row.approver,
        'Evidence required': row.evidence
      });
    },

    open_risk_routing_edit(row) {
      if (!row) {
        return;
      }

      this.open_edit_modal('risk_routing_edit', {
        Trigger: row.trigger,
        'Fires on': 'risk',
        'Risk level (for risk-mode)': row.risk,
        'Add approvers (comma-separated)': row.adds.join(', '),
        Rationale: row.rationale,
        Active: row.status
      });
    },

    open_delegation_edit(row) {
      if (!row) {
        return;
      }

      const [start_date, end_date] = row.period.split(' → ').map((value) => value.trim());

      this.open_edit_modal('delegation_edit', {
        Delegator: row.delegator,
        'Authority role delegated': row.authority,
        Delegate: row.delegate,
        'Scope / limit': row.scope,
        'Start date': start_date,
        'End date': end_date,
        Status: row.status.label
      });
    },

    open_sod_rule_edit(card) {
      if (!card) {
        return;
      }

      const chip_values = card.chips || [];
      const duty_one = duty_value(chip_values, 0);
      const duty_two = duty_value(chip_values, 1);
      const duty_three = duty_value(chip_values, 2);

      this.open_edit_modal('sod_rule_edit', {
        Process: card.title,
        'Duty 1': duty_one,
        'Duty 2': duty_two,
        'Duty 3': duty_three,
        Rule: card.note,
        Status: card.status
      });
    },

    open_sod_conflict_edit(row) {
      if (!row) {
        return;
      }

      this.open_edit_modal('sod_conflict_edit', {
        Person: row.person,
        Process: row.process,
        Conflict: row.conflict,
        Severity: row.severity,
        Status: row.status.label
      });
    },

    open_exception_edit(item) {
      if (!item) {
        return;
      }

      this.open_edit_modal('authority_exception_edit', build_exception_edit_overrides(item));
    },

    close_modal() {
      this.active_modal_key = null;
      this.active_modal_data = null;
      document.body.classList.remove('doa-modal-open');
    },

    clone_modal_definition(modal_key, overrides = {}) {
      const modal = Reflect.get(this.modal_catalog, modal_key);

      if (!modal) {
        return null;
      }

      const fields = (modal.fields || []).map((field) => ({
        ...field,
        options: Array.isArray(field.options) ? [...field.options] : field.options,
        value: Object.hasOwn(overrides, field.label)
          ? Reflect.get(overrides, field.label)
          : field.value || ''
      }));

      const updated_modal = {
        ...modal,
        fields
      };

      return updated_modal;
    },

    normalize_band_value(band, side) {
      if (!band) return '';
      const compact_band = band.replace(/\s+/g, ' ').trim();
      if (compact_band.startsWith('Above ')) return normalize_above_band_value(compact_band, side);
      const range_value = parse_range_band(compact_band, side);
      if (range_value !== null) return range_value;
      const normalized_band_value = digits_only(compact_band);
      return normalized_band_value;
    },

    close_drawer() {
      this.active_drawer_key = null;
    },

    get_progress_dot_class,
    get_progress_width_class
};
export { delegation_of_authority_hooks };

